import {
  ArrowUp,
  Bell,
  BookMarked,
  BookOpen,
  Bot,
  Bookmark,
  CalendarCheck,
  Clock,
  Eye,
  Flame,
  FolderTree,
  Globe,
  Heart,
  Library,
  ImagePlus,
  LayoutGrid,
  Link,
  ListVideo,
  MessageSquare,
  Mic,
  Pencil,
  Pin,
  Play,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  Share2,
  Shuffle,
  Sparkles,
  StickyNote,
  ThumbsUp,
  TrendingUp,
  Trophy,
  Users,
  Video,
} from "lucide-react";
import type { TourStep } from "@/components/onboarding/tour-steps";

/**
 * Per-page tour step definitions.
 *
 * Each tour points at real interactive elements (buttons, search bars, filter
 * controls) using `data-tour` attributes. Tours are 2-5 steps — enough to show
 * the key actions on each page without overwhelming the user.
 *
 * Step content uses action-oriented language: "Click here to...", "Use this
 * to...", "Tap...". This guides the user to actually USE the feature, not just
 * read about it.
 *
 * Copy is localised through `titleKey` / `contentKey`, which `TourTooltip`
 * resolves with `t(key, default)` at render time; the literal `title` /
 * `content` beside each key is that default. Keeping the definitions as plain
 * module-level data (rather than a factory taking `t`) means `PAGE_TOURS` keeps
 * a stable identity across renders — pages pass `steps` straight into
 * `usePageTour`, which registers its starter in context, and a fresh array on
 * every render would re-register (and re-render) the whole provider forever.
 *
 * `target`, `route` and `action.click` stay English: they are DOM hooks, not
 * prose, and translating one silently breaks the spotlight.
 */

interface PageTour {
  routeKey: string;
  steps: TourStep[];
}

export const PAGE_TOURS: Record<string, PageTour> = {
  home: {
    routeKey: "home",
    steps: [
      {
        target: '[data-tour="create-topic"]',
        titleKey: "pages.onboarding.tours.home.createTopic.title",
        title: "Create a practice topic",
        contentKey: "pages.onboarding.tours.home.createTopic.content",
        content:
          "Click here to create your own practice topic. Type in any subject and AI will generate questions and vocabulary for you to practice.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="vocabulary-widget"]',
        titleKey: "pages.onboarding.tours.home.vocabularyWidget.title",
        title: "Vocabulary",
        contentKey: "pages.onboarding.tours.home.vocabularyWidget.content",
        content:
          "Click here to open your saved words. Use spaced-repetition reviews daily to move words into long-term memory.",
        placement: "top",
        data: { icon: BookMarked },
      },
      {
        target: '[data-tour="collections-widget"]',
        titleKey: "pages.onboarding.tours.home.collectionsWidget.title",
        title: "Collections",
        contentKey: "pages.onboarding.tours.home.collectionsWidget.content",
        content:
          "Open vocabulary collections — themed word sets created by you or the community. Browse, save, or create your own to study a topic in depth.",
        placement: "top",
        data: { icon: FolderTree },
      },
      {
        target: '[data-tour="practice-widget"]',
        titleKey: "pages.onboarding.tours.home.practiceWidget.title",
        title: "Practice",
        contentKey: "pages.onboarding.tours.home.practiceWidget.content",
        content:
          "Click here to jump back into your most recently practiced topic, or browse to pick a new one. Aim for at least one session per day to keep your streak alive.",
        placement: "top",
        data: { icon: Mic },
      },
      {
        target: '[data-tour="streak-level-widget"]',
        titleKey: "pages.onboarding.tours.home.streakWidget.title",
        title: "Streak & level",
        contentKey: "pages.onboarding.tours.home.streakWidget.content",
        content:
          "Click here to see your streak and XP progress. Practice daily to keep your streak alive and level up.",
        placement: "top",
        data: { icon: Flame },
      },
      {
        target: '[data-tour="leaderboard-widget"]',
        titleKey: "pages.onboarding.tours.home.leaderboardWidget.title",
        title: "Leaderboard",
        contentKey: "pages.onboarding.tours.home.leaderboardWidget.content",
        content:
          "Click here to see the global leaderboard. Track your ranking by time studied, XP, streak, and vocabulary reviews.",
        placement: "top",
        data: { icon: Trophy },
      },
      {
        target: '[data-tour="videos-widget"]',
        titleKey: "pages.onboarding.tours.home.videosWidget.title",
        title: "Video practice",
        contentKey: "pages.onboarding.tours.home.videosWidget.content",
        content:
          "Click here to browse video lessons. Pick a video to practice shadowing the speaker or typing what you hear (dictation).",
        placement: "top",
        data: { icon: Video },
      },
      {
        target: '[data-tour="ai-assistant-widget"]',
        titleKey: "pages.onboarding.tours.home.aiWidget.title",
        title: "AI topic creation",
        contentKey: "pages.onboarding.tours.home.aiWidget.content",
        content:
          "Click here to chat with the AI and generate new practice topics conversationally. Describe what you want to practice and it suggests topics with questions and vocabulary.",
        placement: "top",
        data: { icon: Bot },
      },
      {
        target: '[data-tour="study-groups-widget"]',
        titleKey: "pages.onboarding.tours.home.studyGroupsWidget.title",
        title: "Your study groups",
        contentKey: "pages.onboarding.tours.home.studyGroupsWidget.content",
        content:
          "Your joined study groups live here in your quick-access widgets. Click any group to view its members, shared topics, and leaderboard. Use 'View All' to browse or create more.",
        placement: "top",
        data: { icon: Users },
      },
      {
        target: '[data-tour="featured-collections"]',
        titleKey: "pages.onboarding.tours.home.featuredCollections.title",
        title: "Collections",
        contentKey: "pages.onboarding.tours.home.featuredCollections.content",
        content:
          "Browse vocabulary collections here — themed word sets curated for popular topics. Click 'View All' to see the full library or create your own.",
        placement: "top",
        data: { icon: FolderTree },
      },
      {
        target: '[data-tour="topic-of-the-day"]',
        titleKey: "pages.onboarding.tours.home.topicOfDay.title",
        title: "Topic of the day",
        contentKey: "pages.onboarding.tours.home.topicOfDay.content",
        content:
          "Every day we pick a featured topic. Click the card to view questions and vocabulary, or hit the practice button to start a speaking session immediately.",
        placement: "top",
        data: { icon: Sparkles },
      },
      {
        target: '[data-tour="group-topic-of-day"]',
        titleKey: "pages.onboarding.tours.home.groupTopicOfDay.title",
        title: "Group topic of the day",
        contentKey: "pages.onboarding.tours.home.groupTopicOfDay.content",
        content:
          "If you're in a study group, this shows the group's current topic. Practice it to contribute to the group leaderboard.",
        placement: "top",
        data: { icon: Users },
      },
      {
        target: "body",
        titleKey: "pages.onboarding.tours.home.allSet.title",
        title: "You're all set",
        contentKey: "pages.onboarding.tours.home.allSet.content",
        content:
          "Pick a topic, practice speaking, and build a daily streak. Use the avatar menu's 'Take a tour' to replay this walkthrough anytime.",
        placement: "center",
        isFixed: true,
        data: { icon: Sparkles },
      },
    ],
  },

  "my-topics": {
    routeKey: "my-topics",
    steps: [
      {
        target: '[data-tour="quick-generate"]',
        titleKey: "pages.onboarding.tours.myTopics.quickGenerate.title",
        title: "Quick Generate",
        contentKey: "pages.onboarding.tours.myTopics.quickGenerate.content",
        content:
          "Click 'Quick Generate' to enter a topic name, pick a category, and immediately generate a full practice set with questions, vocabulary, and optional speaking guide.",
        placement: "top",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="chat-with-ai"]',
        titleKey: "pages.onboarding.tours.myTopics.chatWithAi.title",
        title: "Chat with AI",
        contentKey: "pages.onboarding.tours.myTopics.chatWithAi.content",
        content:
          "Click here to brainstorm topics conversationally with the AI. Describe what you want to practice and it will suggest topics with questions and vocabulary.",
        placement: "top",
        data: { icon: Bot },
      },
      {
        target: '[data-tour="sidebar-history"]',
        titleKey: "pages.onboarding.tours.myTopics.sidebarHistory.title",
        title: "Topic history",
        contentKey: "pages.onboarding.tours.myTopics.sidebarHistory.content",
        content:
          "Every topic you've created or practiced appears here in chronological order. Click any topic to view its questions and vocabulary, or continue practicing. Use 'More' to load older topics.",
        placement: "right",
        data: { icon: Clock },
      },
      {
        target: '[data-tour="recent-topics"]',
        titleKey: "pages.onboarding.tours.myTopics.recentTopics.title",
        title: "Your recent topics",
        contentKey: "pages.onboarding.tours.myTopics.recentTopics.content",
        content:
          "Every topic you generate appears here. Click one to view its questions and vocabulary, or start a practice session. Use the pin icon to keep important topics at the top.",
        placement: "top",
        data: { icon: BookOpen },
      },
    ],
  },

  "my-topics-ai": {
    routeKey: "my-topics-ai",
    steps: [
      {
        target: '[data-tour="ai-chat"]',
        titleKey: "pages.onboarding.tours.myTopicsAi.aiChat.title",
        title: "AI topic generation",
        contentKey: "pages.onboarding.tours.myTopicsAi.aiChat.content",
        content:
          "Chat with the AI here to brainstorm and generate practice topics. Describe what you want to practice, and it will suggest topics with questions and vocabulary.",
        placement: "bottom",
        data: { icon: Bot },
      },
      {
        target: '[data-tour="ai-message-input"]',
        titleKey: "pages.onboarding.tours.myTopicsAi.messageInput.title",
        title: "Type your message",
        contentKey: "pages.onboarding.tours.myTopicsAi.messageInput.content",
        content:
          "Type a topic you want to practice here — for example 'ordering coffee' or 'travel phrases'. Press Enter to send, Shift+Enter for a new line.",
        placement: "top",
        data: { icon: MessageSquare },
      },
      {
        target: '[data-tour="ai-send"]',
        titleKey: "pages.onboarding.tours.myTopicsAi.send.title",
        title: "Send your message",
        contentKey: "pages.onboarding.tours.myTopicsAi.send.content",
        content:
          "Click the send button or press Enter to ask the AI to generate a practice topic. Once ready, a preview panel will appear on the right.",
        placement: "top",
        data: { icon: ArrowUp },
      },
      {
        target: '[data-tour="ai-preview-panel"]',
        titleKey: "pages.onboarding.tours.myTopicsAi.previewPanel.title",
        title: "Review and save",
        contentKey: "pages.onboarding.tours.myTopicsAi.previewPanel.content",
        content:
          "When the AI generates a topic draft, it appears here. Review the questions and vocabulary, then click 'Create Topic' to save it to your topics list.",
        placement: "left",
        data: { icon: Eye },
      },
    ],
  },

  "my-topics-ai-detail": {
    routeKey: "my-topics-ai-detail",
    steps: [
      {
        target: '[data-tour="ai-chat"]',
        titleKey: "pages.onboarding.tours.myTopicsAiDetail.aiChat.title",
        title: "Continue your conversation",
        contentKey: "pages.onboarding.tours.myTopicsAiDetail.aiChat.content",
        content:
          "This is your ongoing AI conversation. Keep chatting to refine your topic, ask for adjustments, or create more practice material.",
        placement: "bottom",
        data: { icon: Bot },
      },
      {
        target: '[data-tour="ai-message-input"]',
        titleKey: "pages.onboarding.tours.myTopicsAiDetail.messageInput.title",
        title: "Send a follow-up",
        contentKey: "pages.onboarding.tours.myTopicsAiDetail.messageInput.content",
        content:
          "Type a follow-up message to adjust the topic — ask for harder questions, different vocabulary, or a new angle on the same subject.",
        placement: "top",
        data: { icon: MessageSquare },
      },
      {
        target: '[data-tour="ai-preview-panel"]',
        titleKey: "pages.onboarding.tours.myTopicsAiDetail.previewPanel.title",
        title: "Review drafts",
        contentKey: "pages.onboarding.tours.myTopicsAiDetail.previewPanel.content",
        content:
          "Topic drafts from this conversation appear here. Click 'Create Topic' to save a draft, or use 'View' / 'Edit' to open a topic you've already saved.",
        placement: "left",
        data: { icon: Eye },
      },
    ],
  },

  "my-topic-detail": {
    routeKey: "my-topic-detail",
    steps: [
      {
        target: '[data-tour="practice-topic"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.practiceTopic.title",
        title: "Start practicing",
        contentKey: "pages.onboarding.tours.myTopicDetail.practiceTopic.content",
        content:
          "Click 'View' to open the practice session for this topic. You'll be able to record yourself answering questions and get instant feedback on fluency and pronunciation.",
        placement: "bottom",
        data: { icon: Mic },
      },
      {
        target: '[data-tour="regenerate-topic"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.regenerateTopic.title",
        title: "Regenerate content",
        contentKey: "pages.onboarding.tours.myTopicDetail.regenerateTopic.content",
        content:
          "Click 'Regenerate' to refresh the questions, vocabulary, and speaking guide for this topic. Useful if you want more practice material or the content feels too easy or hard.",
        placement: "bottom",
        data: { icon: RefreshCw },
      },
      {
        target: '[data-tour="pin-topic"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.pinTopic.title",
        title: "Pin for quick access",
        contentKey: "pages.onboarding.tours.myTopicDetail.pinTopic.content",
        content:
          "Click the pin icon to keep this topic at the top of your sidebar list. Pinned topics are always easy to find when you want to practice them again.",
        placement: "bottom",
        data: { icon: Pin },
      },
      {
        target: '[data-tour="share-topic"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.shareTopic.title",
        title: "Share with others",
        contentKey: "pages.onboarding.tours.myTopicDetail.shareTopic.content",
        content:
          "Click 'Copy Link' to share this topic with friends or classmates. Make it public so anyone can discover and practice it.",
        placement: "bottom",
        data: { icon: Link },
      },
      {
        target: '[data-tour="share-to-group"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.shareToGroup.title",
        title: "Share with your study group",
        contentKey: "pages.onboarding.tours.myTopicDetail.shareToGroup.content",
        content:
          "Click 'Share to Group' to share this topic with a study group you belong to. Group members can then practice it together and earn points on the group leaderboard.",
        placement: "bottom",
        data: { icon: Users },
      },
      {
        target: '[data-tour="topic-banner"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.topicBanner.title",
        title: "Customize with a banner",
        contentKey: "pages.onboarding.tours.myTopicDetail.topicBanner.content",
        content:
          "Click here to add or change the topic banner image. You can upload your own image or search Unsplash to make your topic visually stand out.",
        placement: "left",
        data: { icon: ImagePlus },
      },
      {
        target: '[data-tour="detail-tabs"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.detailTabs.title",
        title: "Browse content tabs",
        contentKey: "pages.onboarding.tours.myTopicDetail.detailTabs.content",
        content:
          "Switch between Questions, Vocabulary, and Speaking Guide using these tabs. Each tab shows the content AI generated for this topic.",
        placement: "top",
        data: { icon: LayoutGrid },
      },
      {
        target: '[data-tour="detail-content"]',
        titleKey: "pages.onboarding.tours.myTopicDetail.detailContent.title",
        title: "Practice content",
        contentKey: "pages.onboarding.tours.myTopicDetail.detailContent.content",
        content:
          "The main content area shows questions, vocabulary words, or speaking tips depending on which tab is active. Click any item to practice or review it.",
        placement: "top",
        data: { icon: BookOpen },
      },
    ],
  },

  topics: {
    routeKey: "topics",
    steps: [
      {
        target: '[data-tour="create-topic"]',
        titleKey: "pages.onboarding.tours.topics.createTopic.title",
        title: "Create a topic",
        contentKey: "pages.onboarding.tours.topics.createTopic.content",
        content:
          "Click 'Create Topic' to create your own practice topic. Type in any subject and AI will generate questions and vocabulary for you to practice.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="search-topics"]',
        titleKey: "pages.onboarding.tours.topics.searchTopics.title",
        title: "Search topics",
        contentKey: "pages.onboarding.tours.topics.searchTopics.content",
        content:
          "Type here to search topics by title. Results update instantly as you type. Click the X to clear your search.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="filter-tabs"]',
        titleKey: "pages.onboarding.tours.topics.filterTabs.title",
        title: "Filter by category",
        contentKey: "pages.onboarding.tours.topics.filterTabs.content",
        content:
          "Use these tabs to switch between All, Recent, Trending, Popular and Bookmarked topics. The category sidebar on the left lets you drill down further.",
        placement: "bottom",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="topics-grid"]',
        titleKey: "pages.onboarding.tours.topics.topicsGrid.title",
        title: "Pick a topic to practice",
        contentKey: "pages.onboarding.tours.topics.topicsGrid.content",
        content:
          "Click any topic card to view its questions and vocabulary. Use the practice button on each card to jump straight into a speaking session.",
        placement: "top",
        data: { icon: BookOpen },
      },
    ],
  },

  vocabulary: {
    routeKey: "vocabulary",
    steps: [
      {
        target: '[data-tour="vocab-tabs"]',
        titleKey: "pages.onboarding.tours.vocabulary.vocabTabs.title",
        title: "Switch between views",
        contentKey: "pages.onboarding.tours.vocabulary.vocabTabs.content",
        content:
          "Use these tabs to switch between 'My Words' and 'Collections'. Collections let you group words into themed sets for focused study.",
        placement: "bottom",
        data: { icon: LayoutGrid },
      },
      {
        target: '[data-tour="add-word"]',
        titleKey: "pages.onboarding.tours.vocabulary.addWord.title",
        title: "Add new words",
        contentKey: "pages.onboarding.tours.vocabulary.addWord.content",
        content:
          "Click 'Add Word' to generate new vocabulary with AI. You can enter a word or topic, and it will create definitions, examples, and translations.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="vocab-daily-review"]',
        titleKey: "pages.onboarding.tours.vocabulary.dailyReview.title",
        title: "Daily review reminder",
        contentKey: "pages.onboarding.tours.vocabulary.dailyReview.content",
        content:
          "When you have words due, this amber card reminds you to review them. The big number is how many are waiting, and each bar below is one topic's share of the queue — click a row, or 'Review everything', to start.",
        placement: "top",
        data: { icon: Sparkles },
      },
      {
        target: '[data-tour="vocab-search"]',
        titleKey: "pages.onboarding.tours.vocabulary.searchWords.title",
        title: "Search your words",
        contentKey: "pages.onboarding.tours.vocabulary.searchWords.content",
        content:
          "Type here to search through your saved words by name or definition. Results update instantly as you type.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="vocab-filter-pills"]',
        titleKey: "pages.onboarding.tours.vocabulary.filterPills.title",
        title: "Filter by review status",
        contentKey: "pages.onboarding.tours.vocabulary.filterPills.content",
        content:
          "Use these pills to filter your deck by All, Due (words ready for review), Reviewed, No Review, or Incomplete (missing info). Each pill shows the count for that status.",
        placement: "top",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="vocab-stats"]',
        titleKey: "pages.onboarding.tours.vocabulary.stats.title",
        title: "Your study stats",
        contentKey: "pages.onboarding.tours.vocabulary.stats.content",
        content:
          "Track your vocabulary progress here: total saved, due for review, reviewed today, and words needing enrichment.",
        placement: "top",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="vocab-learning-path"]',
        titleKey: "pages.onboarding.tours.vocabulary.learningPath.title",
        title: "Learning path",
        contentKey: "pages.onboarding.tours.vocabulary.learningPath.content",
        content:
          "See your vocabulary mastery breakdown across four tiers: New, Learning, Reviewing, and Mastered. Click any tier to filter your deck by that level.",
        placement: "top",
        data: { icon: TrendingUp },
      },
    ],
  },

  "vocabulary-notes": {
    routeKey: "vocabulary-notes",
    steps: [
      {
        target: '[data-tour="notes-new"]',
        titleKey: "pages.onboarding.tours.vocabularyNotes.notesNew.title",
        title: "Create a note",
        contentKey: "pages.onboarding.tours.vocabularyNotes.notesNew.content",
        content:
          "Click 'New note' to jot down a free-form personal note — ideas, reminders, or anything you want to remember. It opens in a rich text editor.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="vocab-tabs"]',
        titleKey: "pages.onboarding.tours.vocabularyNotes.vocabTabs.title",
        title: "Switch between views",
        contentKey: "pages.onboarding.tours.vocabularyNotes.vocabTabs.content",
        content:
          "Use these tabs to move between 'My Words', 'Collections', and 'Notes'. You're on Notes now — your word notes and personal notes live here.",
        placement: "bottom",
        data: { icon: LayoutGrid },
      },
      {
        target: '[data-tour="notes-search"]',
        titleKey: "pages.onboarding.tours.vocabularyNotes.notesSearch.title",
        title: "Search your notes",
        contentKey: "pages.onboarding.tours.vocabularyNotes.notesSearch.content",
        content:
          "Type here to search across both word notes and personal notes by keyword. Results update instantly as you type.",
        placement: "bottom",
        data: { icon: Search },
        hideIfMissing: true,
      },
      {
        target: '[data-tour="notes-filters"]',
        titleKey: "pages.onboarding.tours.vocabularyNotes.notesFilters.title",
        title: "Filter by type",
        contentKey: "pages.onboarding.tours.vocabularyNotes.notesFilters.content",
        content:
          "Use these pills to show All notes, just 'Word notes' (attached to vocabulary you studied), or just 'Personal' notes. Each pill shows its count.",
        placement: "top",
        data: { icon: TrendingUp },
        hideIfMissing: true,
      },
      {
        target: '[data-tour="notes-grid"]',
        titleKey: "pages.onboarding.tours.vocabularyNotes.notesGrid.title",
        title: "Your notes",
        contentKey: "pages.onboarding.tours.vocabularyNotes.notesGrid.content",
        content:
          "All your notes appear here as cards, newest first. Click any card to edit it inline, or use the menu to delete it.",
        placement: "top",
        data: { icon: StickyNote },
        hideIfMissing: true,
      },
    ],
  },

  "vocabulary-collections": {
    routeKey: "vocabulary-collections",
    steps: [
      {
        target: '[data-tour="create-collection"]',
        titleKey: "pages.onboarding.tours.vocabularyCollections.createCollection.title",
        title: "Create a collection",
        contentKey: "pages.onboarding.tours.vocabularyCollections.createCollection.content",
        content:
          "Click 'Create Collection' to build a new themed word set. Add words manually or generate them with AI, then track your mastery as you review.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="collections-stats"]',
        titleKey: "pages.onboarding.tours.vocabularyCollections.stats.title",
        title: "Your stats at a glance",
        contentKey: "pages.onboarding.tours.vocabularyCollections.stats.content",
        content:
          "See community sets, words available, your collections, and in-progress counts. Use this to track how much vocabulary you're building across all collections.",
        placement: "top",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="collections-search"]',
        titleKey: "pages.onboarding.tours.vocabularyCollections.search.title",
        title: "Search collections",
        contentKey: "pages.onboarding.tours.vocabularyCollections.search.content",
        content:
          "Type here to search collections by title or description. Results update as you type.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="collections-filter-pills"]',
        titleKey: "pages.onboarding.tours.vocabularyCollections.filterPills.title",
        title: "Filter collections",
        contentKey: "pages.onboarding.tours.vocabularyCollections.filterPills.content",
        content:
          "Use these pills to filter collections by All, Mine, Public, Shared, or Topics. Each pill shows the count for that category.",
        placement: "top",
        data: { icon: TrendingUp },
      },
    ],
  },

  "vocabulary-create-collection": {
    routeKey: "vocabulary-create-collection",
    steps: [
      {
        target: '[data-tour="collection-title"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionTitle.title",
        title: "Name your collection",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionTitle.content",
        content:
          "Give your collection a clear title — like 'Business English' or 'IELTS Prep'. This is how you'll find it later in your collections list.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="collection-visibility"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionVisibility.title",
        title: "Set visibility",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionVisibility.content",
        content:
          "Public collections appear in the community directory. Private collections are only visible to you. You can change this anytime.",
        placement: "bottom",
        data: { icon: Globe },
      },
      {
        target: '[data-tour="collection-word-source"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionWordSource.title",
        title: "Choose your word source",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionWordSource.content",
        content:
          "Pick from your saved vocabulary or type new words to generate definitions with AI. Mix and match both sources in a single collection.",
        placement: "bottom",
        // The two word-source tabs are matched by position, not by their
        // `aria-label`. That label is user-visible copy
        // (`pages.vocabularyCollections.creator.*`), so it changes with the
        // locale — a selector built from it silently stopped matching once
        // this step was localised, and a step that can't click its own target
        // spotlights a panel the tour never opened.
        action: { click: '[role="tab"]:first-of-type' },
        data: { icon: BookOpen },
      },
      {
        target: '[data-tour="collection-word-search"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionWordSearch.title",
        title: "Search and select words",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionWordSearch.content",
        content:
          "Search your saved vocabulary by word or definition. Filter by level or type, then check the words you want to include in this collection.",
        placement: "top",
        action: { click: '[role="tab"]:first-of-type', scope: '[data-tour="collection-word-source"]' },
        data: { icon: Search },
      },
      {
        target: '[data-tour="collection-inline-input"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionInlineInput.title",
        title: "Add new words",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionInlineInput.content",
        content:
          "Type any word and press Enter to queue it up. Then click 'Generate' to create definitions, examples, and IPA — all powered by AI.",
        placement: "top",
        action: { click: '[role="tab"]:last-of-type', scope: '[data-tour="collection-word-source"]' },
        data: { icon: Plus },
      },
      {
        target: '[data-tour="collection-inline-area"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionInlineArea.title",
        title: "Generate definitions",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionInlineArea.content",
        content:
          "Click 'Generate' to create definitions, examples, and IPA for your queued words. Review the results, check the ones you want, and add them to your collection.",
        placement: "top",
        data: { icon: Sparkles },
      },
      {
        target: '[data-tour="collection-your-deck"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionYourDeck.title",
        title: "Your deck",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionYourDeck.content",
        content:
          "This sidebar shows every word in your collection as cards. You can remove words from here. The count updates live as you add or remove words from either source.",
        placement: "left",
        data: { icon: BookOpen },
      },
      {
        target: '[data-tour="collection-action"]',
        titleKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionAction.title",
        title: "Review and save",
        contentKey: "pages.onboarding.tours.vocabularyCreateCollection.collectionAction.content",
        content:
          "Once you've added words, click 'Review' to check your selection. Then hit 'Create Collection' to save — it'll appear on your collections page.",
        placement: "top",
        data: { icon: Sparkles },
      },
    ],
  },

  "vocabulary-collection-detail": {
    routeKey: "vocabulary-collection-detail",
    steps: [
      {
        target: '[data-tour="detail-practice"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailPractice.title",
        title: "Start practicing",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailPractice.content",
        content:
          "Click 'Practice' to begin reviewing this collection's words in flashcard and quiz mode. Your progress and mastery will update as you study.",
        placement: "top",
        data: { icon: Sparkles },
      },
      {
        target: '[data-tour="detail-add-words"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailAddWords.title",
        title: "Add more words",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailAddWords.content",
        content:
          "Click 'Add Words' to add more vocabulary to this collection. Pick from your saved words or generate new ones with AI.",
        placement: "top",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="detail-share"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailShare.title",
        title: "Share this collection",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailShare.content",
        content:
          "Click 'Share' to copy a link to this collection so others can discover and import it. Share it on social media or send it directly to friends.",
        placement: "top",
        data: { icon: Link },
      },
      {
        target: '[data-tour="detail-share-group"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailShareGroup.title",
        title: "Share to a study group",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailShareGroup.content",
        content:
          "Click 'Share to Group' to make this collection available to members of one of your study groups. Everyone in the group can import and practice it.",
        placement: "top",
        data: { icon: Users },
      },
      {
        target: '[data-tour="detail-edit"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailEdit.title",
        title: "Edit collection",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailEdit.content",
        content:
          "Click 'Edit' to change the title, description, visibility, or rearrange the words in this collection.",
        placement: "top",
        data: { icon: BookMarked },
      },
      {
        target: '[data-tour="detail-delete"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailDelete.title",
        title: "Delete collection",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailDelete.content",
        content:
          "Click 'Delete' to permanently remove this collection. This action cannot be undone — all words will be removed from the collection.",
        placement: "top",
        data: { icon: BookMarked },
      },
      {
        target: '[data-tour="detail-stats"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailStats.title",
        title: "Your progress at a glance",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailStats.content",
        content:
          "Review total words, how many you've reviewed, your mastery percentage, and how many are saved to your library. The mastery bar fills as you study.",
        placement: "top",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="detail-search"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailSearch.title",
        title: "Search words",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailSearch.content",
        content:
          "Search any word, definition, or example in this collection. Results update as you type — a quick way to find specific vocabulary.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="detail-filter-pills"]',
        titleKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailFilterPills.title",
        title: "Filter by review status",
        contentKey: "pages.onboarding.tours.vocabularyCollectionDetail.detailFilterPills.content",
        content:
          "Segment words by All, Unreviewed, Reviewed, or Mastered. Each pill shows the count for that category — use them to focus on what needs attention.",
        placement: "top",
        data: { icon: TrendingUp },
      },
    ],
  },

  "vocabulary-practice": {
    routeKey: "vocabulary-practice",
    steps: [
      {
        target: '[data-tour="practice-mode-tabs"]',
        titleKey: "pages.onboarding.tours.vocabularyPractice.practiceModeTabs.title",
        title: "Choose your practice mode",
        contentKey: "pages.onboarding.tours.vocabularyPractice.practiceModeTabs.content",
        content:
          "Pick from Flashcards, Writing, Listening, Word Scramble, and Pronunciation. Writing combines typing the answer and filling in the blank, and Pronunciation has you say the word aloud to check your pronunciation — each mode exercises a different skill, try them all to build comprehensive understanding.",
        placement: "bottom",
        data: { icon: LayoutGrid },
      },
      {
        target: '[data-tour="practice-toolbar"]',
        titleKey: "pages.onboarding.tours.vocabularyPractice.practiceToolbar.title",
        title: "Practice controls",
        contentKey: "pages.onboarding.tours.vocabularyPractice.practiceToolbar.content",
        content:
          "Reverse the card direction, shuffle the word order, and play audio pronunciation. Use the shortcuts popup to learn keyboard commands for faster practice.",
        placement: "bottom",
        data: { icon: Shuffle },
      },
      {
        target: '[data-tour="practice-card"]',
        titleKey: "pages.onboarding.tours.vocabularyPractice.practiceCard.title",
        title: "Study the card",
        contentKey: "pages.onboarding.tours.vocabularyPractice.practiceCard.content",
        content:
          "Read the word or definition. Click or press Space to flip the card and reveal the other side. Take your time — accuracy matters more than speed.",
        placement: "top",
        data: { icon: Eye },
      },
      {
        target: '[data-tour="practice-review"]',
        titleKey: "pages.onboarding.tours.vocabularyPractice.practiceReview.title",
        title: "Rate your recall",
        contentKey: "pages.onboarding.tours.vocabularyPractice.practiceReview.content",
        content:
          "After flipping, pick one of four difficulty levels: Again (missed it completely), Hard (struggled), Good (got it with some effort), or Easy (instant recall). This trains the spaced repetition algorithm to schedule your next review at the perfect time.",
        placement: "top",
        data: { icon: ThumbsUp },
      },
      {
        target: '[data-tour="practice-sidebar"]',
        titleKey: "pages.onboarding.tours.vocabularyPractice.practiceSidebar.title",
        title: "Track your progress",
        contentKey: "pages.onboarding.tours.vocabularyPractice.practiceSidebar.content",
        content:
          "The sidebar shows all words in your practice session. The highlighted one is current; words with a checkmark have been reviewed. Click any word to jump to it.",
        placement: "left",
        data: { icon: ListVideo },
      },
    ],
  },

  "study-groups": {
    routeKey: "study-groups",
    steps: [
      {
        target: '[data-tour="create-group"]',
        titleKey: "pages.onboarding.tours.studyGroups.createGroup.title",
        title: "Create your own group",
        contentKey: "pages.onboarding.tours.studyGroups.createGroup.content",
        content:
          "Click 'Create Group' to start a study group. Set a name, description, and visibility — then invite friends or make it public for anyone to join.",
        placement: "bottom",
        data: { icon: Plus },
      },
      {
        target: '[data-tour="search-groups"]',
        titleKey: "pages.onboarding.tours.studyGroups.searchGroups.title",
        title: "Find a group",
        contentKey: "pages.onboarding.tours.studyGroups.searchGroups.content",
        content:
          "Search for groups by name or description. The results update instantly as you type.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="groups-tab-my"]',
        titleKey: "pages.onboarding.tours.studyGroups.tabMy.title",
        title: "My Groups",
        contentKey: "pages.onboarding.tours.studyGroups.tabMy.content",
        content:
          "Your groups appear here — groups you've joined or created. Click a card to view its members, shared topics, and leaderboard.",
        placement: "bottom",
        data: { icon: Users },
        action: {
          scope: '[role="tablist"]',
          click: '[data-tour="groups-tab-my"]',
        },
      },
      {
        target: '[data-tour="groups-tab-all"]',
        titleKey: "pages.onboarding.tours.studyGroups.tabAll.title",
        title: "All Groups",
        contentKey: "pages.onboarding.tours.studyGroups.tabAll.content",
        content:
          "Browse every public group here. Check the seat count and privacy on each card, then join straight from the card — or open one first to see what its members are studying.",
        placement: "bottom",
        data: { icon: Globe },
        action: {
          scope: '[role="tablist"]',
          click: '[data-tour="groups-tab-all"]',
        },
      },
    ],
  },

  leaderboard: {
    routeKey: "leaderboard",
    steps: [
      {
        target: '[data-tour="leaderboard-metrics"]',
        titleKey: "pages.onboarding.tours.leaderboard.metrics.title",
        title: "Pick a board",
        contentKey: "pages.onboarding.tours.leaderboard.metrics.content",
        content:
          "Four boards rank the same learners by different numbers: XP earned, practice time, vocabulary, and consistency. Switch between them here.",
        placement: "bottom",
        data: { icon: Trophy },
      },
      {
        target: '[data-tour="leaderboard-periods"]',
        titleKey: "pages.onboarding.tours.leaderboard.periods.title",
        title: "Change the window",
        contentKey: "pages.onboarding.tours.leaderboard.periods.content",
        content:
          "Today, this week, this month or all time. The ranking, the podium and your own position all follow the window you pick.",
        placement: "bottom",
        data: { icon: Clock },
      },
      {
        target: '[data-tour="leaderboard-sections"]',
        titleKey: "pages.onboarding.tours.leaderboard.board.title",
        title: "Read the board",
        contentKey: "pages.onboarding.tours.leaderboard.board.content",
        content:
          "The top three stand on the podium. Everyone below them carries a bar showing their share of the leader's score, and your own position stays pinned at the end if you are further down.",
        placement: "top",
        data: { icon: Trophy },
      },
    ],
  },

  videos: {
    routeKey: "videos",
    steps: [
      {
        target: '[data-tour="request-video"]',
        titleKey: "pages.onboarding.tours.videos.requestVideo.title",
        title: "Request a video",
        contentKey: "pages.onboarding.tours.videos.requestVideo.content",
        content:
          "Can't find what you're looking for? Click 'Request a Video' to suggest a topic. New videos are added based on community requests.",
        placement: "bottom",
        data: { icon: Sparkles },
      },
      {
        target: '[data-tour="search-videos"]',
        titleKey: "pages.onboarding.tours.videos.searchVideos.title",
        title: "Search videos",
        contentKey: "pages.onboarding.tours.videos.searchVideos.content",
        content:
          "Type here to search for videos by title. Results update instantly as you type.",
        placement: "bottom",
        data: { icon: Search },
      },
      {
        target: '[data-tour="level-tabs"]',
        titleKey: "pages.onboarding.tours.videos.levelTabs.title",
        title: "Filter by level",
        contentKey: "pages.onboarding.tours.videos.levelTabs.content",
        content:
          "Use these tabs to filter videos by certification level (A1–C2). Pick the level that matches your current ability.",
        placement: "bottom",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="video-list"]',
        titleKey: "pages.onboarding.tours.videos.videoList.title",
        title: "Pick a video",
        contentKey: "pages.onboarding.tours.videos.videoList.content",
        content:
          "Click any video card to open the practice page. You'll be able to watch the video, then switch between speaking and writing practice modes.",
        placement: "top",
        data: { icon: PlayCircle },
      },
    ],
  },

  "video-detail": {
    routeKey: "video-detail",
    steps: [
      {
        target: '[data-tour="video-player"]',
        titleKey: "pages.onboarding.tours.videoDetail.videoPlayer.title",
        title: "Watch the video",
        contentKey: "pages.onboarding.tours.videoDetail.videoPlayer.content",
        content:
          "Watch the video first to get familiar with the content. Then switch modes below to practice shadowing the captions or typing what you hear (dictation).",
        placement: "bottom",
        data: { icon: PlayCircle },
      },
      {
        target: '[data-tour="mode-tabs"]',
        titleKey: "pages.onboarding.tours.videoDetail.modeTabs.title",
        title: "Choose your practice mode",
        contentKey: "pages.onboarding.tours.videoDetail.modeTabs.content",
        content:
          "Click 'Speaking' to record yourself shadowing each caption, or 'Dictation' to type what you hear. The counts show how many captions you've practiced in each mode.",
        placement: "bottom",
        data: { icon: ListVideo },
      },
      {
        target: '[data-tour="caption-list"]',
        titleKey: "pages.onboarding.tours.videoDetail.captionList.title",
        title: "Caption list",
        contentKey: "pages.onboarding.tours.videoDetail.captionList.content",
        content:
          "All captions appear here. Click any caption to jump the video to that timestamp. In speaking mode, you can toggle between masked and full-text views.",
        placement: "left",
        data: { icon: ListVideo },
      },
    ],
  },

  "my-topics-time": {
    routeKey: "my-topics-time",
    steps: [
      {
        target: '[data-tour="time-stats"]',
        titleKey: "pages.onboarding.tours.myTopicsTime.timeStats.title",
        title: "Your study analytics",
        contentKey: "pages.onboarding.tours.myTopicsTime.timeStats.content",
        content:
          "This shows your total practice time, number of topics, average per topic, and category breakdown. Use the sort buttons to reorder the topic list by Most Time, Recent, or A-Z.",
        placement: "bottom",
        data: { icon: Clock },
      },
      {
        target: '[data-tour="sort-options"]',
        titleKey: "pages.onboarding.tours.myTopicsTime.sortOptions.title",
        title: "Sort your topics",
        contentKey: "pages.onboarding.tours.myTopicsTime.sortOptions.content",
        content:
          "Click 'Most Time', 'Recent', or 'A-Z' to reorder your topic list. Use this to find where to focus your practice next.",
        placement: "bottom",
        data: { icon: ArrowUp },
      },
    ],
  },

  "study-group-detail": {
    routeKey: "study-group-detail",
    steps: [
      {
        target: '[data-tour="tab-topics"]',
        titleKey: "pages.onboarding.tours.studyGroupDetail.tabTopics.title",
        title: "Shared topics",
        contentKey: "pages.onboarding.tours.studyGroupDetail.tabTopics.content",
        content:
          "Members share practice topics here. Each card shows how many questions and words it holds, so you can judge the size of a topic before opening it. The group's focus for today is flagged.",
        placement: "top",
        data: { icon: BookOpen },
        action: {
          click: '[data-tour="tab-topics"]',
        },
      },
      {
        target: '[data-tour="share-button"]',
        titleKey: "pages.onboarding.tours.studyGroupDetail.shareButton.title",
        title: "Share with the group",
        contentKey: "pages.onboarding.tours.studyGroupDetail.shareButton.content",
        content:
          "Sharing is this group's main action. Click Share a Topic to add something you have already built — the label follows the tab you are on, so switch to Collections to share a word list instead.",
        placement: "top",
        data: { icon: Share2 },
      },
      {
        target: '[data-tour="tab-collections"]',
        titleKey: "pages.onboarding.tours.studyGroupDetail.tabCollections.title",
        title: "Vocabulary collections",
        contentKey: "pages.onboarding.tours.studyGroupDetail.tabCollections.content",
        content:
          "Vocabulary collections shared with the group. Each card leads with its word count, and the collection of the day is the one the group agreed to drill. Use the search and category filters above the grid to narrow the list.",
        placement: "top",
        data: { icon: Library },
        action: {
          click: '[data-tour="tab-collections"]',
        },
      },
      {
        target: '[data-tour="plan-button"]',
        titleKey: "pages.onboarding.tours.studyGroupDetail.planButton.title",
        title: "Set a practice plan",
        contentKey: "pages.onboarding.tours.studyGroupDetail.planButton.content",
        content:
          "Plan which topics you'll practise on specific days. Members can see who is practising what and when, which makes it easy to arrange a shared study session.",
        placement: "top",
        data: { icon: CalendarCheck },
      },
    ],
  },

  "shared-session": {
    routeKey: "shared-session",
    steps: [
      {
        target: '[data-tour="session-topic"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionTopic.title",
        title: "Topic details",
        contentKey: "pages.onboarding.tours.sharedSession.sessionTopic.content",
        content:
          "Here's the topic with its questions and vocabulary. Scroll down to see everything you can practice.",
        placement: "bottom",
        data: { icon: BookOpen },
      },
      {
        target: '[data-tour="session-stats"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionStats.title",
        title: "Session stats",
        contentKey: "pages.onboarding.tours.sharedSession.sessionStats.content",
        content:
          "See how many people have viewed this topic, join the comments, and track your study time here too.",
        placement: "bottom",
        data: { icon: TrendingUp },
      },
      {
        target: '[data-tour="session-like"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionLike.title",
        title: "Like this topic",
        contentKey: "pages.onboarding.tours.sharedSession.sessionLike.content",
        content:
          "Show appreciation for topics you enjoy. Liking helps others discover quality content.",
        placement: "bottom",
        data: { icon: Heart },
      },
      {
        target: '[data-tour="session-bookmark"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionBookmark.title",
        title: "Bookmark for later",
        contentKey: "pages.onboarding.tours.sharedSession.sessionBookmark.content",
        content:
          "Save this topic to your bookmarks so you can easily find it again. Access your saved topics from the home page.",
        placement: "bottom",
        data: { icon: Bookmark },
      },
      {
        target: '[data-tour="session-share"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionShare.title",
        title: "Share with others",
        contentKey: "pages.onboarding.tours.sharedSession.sessionShare.content",
        content:
          "Share this topic with friends or on social media. Anyone with the link can practice and learn from it.",
        placement: "bottom",
        data: { icon: Share2 },
      },
      {
        target: '[data-tour="session-edit"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionEdit.title",
        title: "Edit topic",
        contentKey: "pages.onboarding.tours.sharedSession.sessionEdit.content",
        content:
          "Only you can see this button. Click to edit the topic title, questions, vocabulary, or update the speaking guide.",
        placement: "bottom",
        data: { icon: Pencil },
        hideIfMissing: true,
      },
      {
        target: '[data-tour="session-progress"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionProgress.title",
        title: "Your progress",
        contentKey: "pages.onboarding.tours.sharedSession.sessionProgress.content",
        content:
          "Track how many questions you've practiced and vocabulary you've reviewed as you work through the topic.",
        placement: "top",
        data: { icon: Trophy },
      },
      {
        target: '[data-tour="session-practice-ai"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionPracticeAi.title",
        title: "Practice with AI",
        contentKey: "pages.onboarding.tours.sharedSession.sessionPracticeAi.content",
        content:
          "Have a conversation about this topic with an AI tutor. You'll get real-time feedback on fluency, pronunciation, and vocabulary usage.",
        placement: "top",
        data: { icon: Bot },
      },
      {
        target: '[data-tour="session-practice-vocab"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionPracticeVocab.title",
        title: "Practice vocabulary",
        contentKey: "pages.onboarding.tours.sharedSession.sessionPracticeVocab.content",
        content:
          "Review and memorize all vocabulary words from this topic in flashcard and quiz mode. Master the words before moving on.",
        placement: "top",
        data: { icon: BookOpen },
      },
      {
        target: 'main.min-w-0 [data-tour="session-question-card"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionQuestions.title",
        title: "Questions",
        contentKey: "pages.onboarding.tours.sharedSession.sessionQuestions.content",
        content:
          "Browse all practice questions for this topic. Use the difficulty filter to focus on Beginner, Intermediate, or Advanced questions. Click a card to reveal the full question, answer, and example response.",
        placement: "top",
        data: { icon: MessageSquare },
      },
      {
        target: 'main.min-w-0 [data-tour="session-question-note"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionQuestionNote.title",
        title: "Question notes",
        contentKey: "pages.onboarding.tours.sharedSession.sessionQuestionNote.content",
        content:
          "Click the note icon to add a private note to any question. Notes help you remember context, tricky points, or personal connections to the material.",
        placement: "bottom",
        data: { icon: StickyNote },
        hideIfMissing: true,
      },
      {
        target: 'aside [data-tour="session-vocab-item"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionVocab.title",
        title: "Vocabulary",
        contentKey: "pages.onboarding.tours.sharedSession.sessionVocab.content",
        content:
          "Review all vocabulary words from this topic. Click any word to see its definition, example, and pronunciation. Filter by CEFR level using the pills above.",
        placement: "top",
        data: { icon: BookOpen },
      },
      {
        target: 'aside [data-tour="session-vocab-note"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionVocabNote.title",
        title: "Add notes",
        contentKey: "pages.onboarding.tours.sharedSession.sessionVocabNote.content",
        content:
          "Click the note icon to add a private note to any vocabulary word. Notes are saved to your account and help you remember context, usage, or tricky details.",
        placement: "bottom",
        data: { icon: StickyNote },
        hideIfMissing: true,
      },
      {
        target: 'aside [data-tour="session-vocab-save"]',
        titleKey: "pages.onboarding.tours.sharedSession.sessionVocabSave.title",
        title: "Save vocabulary",
        contentKey: "pages.onboarding.tours.sharedSession.sessionVocabSave.content",
        content:
          "Click the bookmark icon to save a word to your personal vocabulary library. Saved words appear in your vocabulary section for focused review.",
        placement: "bottom",
        data: { icon: Bookmark },
        hideIfMissing: true,
      },
      {
        target: "aside #speaking-guide",
        titleKey: "pages.onboarding.tours.sharedSession.sessionGuide.title",
        title: "Teacher's notes",
        contentKey: "pages.onboarding.tours.sharedSession.sessionGuide.content",
        content:
          "Tap to expand the Speaking Guide — a quick reference with natural expressions, sentence starters, conversation fillers, and grammar tips. Use these to sound more fluent during practice.",
        placement: "top",
        data: { icon: Sparkles },
        action: {
          click: '[data-tour="session-guide-expand"]',
        },
        hideIfMissing: true,
      },
    ],
  },

  notifications: {
    routeKey: "notifications",
    steps: [
      {
        target: "body",
        titleKey: "pages.onboarding.tours.notifications.bell.title",
        title: "Notifications",
        contentKey: "pages.onboarding.tours.notifications.bell.content",
        content:
          "Review reminders, group activity, and other updates show up here. Keep an eye on the bell icon in the top bar.",
        placement: "center",
        isFixed: true,
        data: { icon: Bell },
      },
    ],
  },

  "my-speaking": {
    routeKey: "my-speaking",
    steps: [
      {
        target: '[data-tour="page-hero"]',
        titleKey: "pages.onboarding.tours.mySpeaking.pageHero.title",
        title: "My Speaking",
        contentKey: "pages.onboarding.tours.mySpeaking.pageHero.content",
        content:
          "Every spoken answer you record across your topics is collected here. Revisit them anytime to hear yourself back and review the AI feedback.",
        placement: "bottom",
        data: { icon: Mic },
      },
      {
        target: '[data-tour="speaking-topic-card"]',
        titleKey: "pages.onboarding.tours.mySpeaking.topicCard.title",
        title: "Your recorded topics",
        contentKey: "pages.onboarding.tours.mySpeaking.topicCard.content",
        content:
          "Each card shows the topic and your average spoken-score badge. Click a card to expand it and replay every recorded answer with its AI feedback.",
        placement: "top",
        hideIfMissing: true,
        data: { icon: Mic },
      },
      {
        target: '[data-tour="speaking-recordings"]',
        titleKey: "pages.onboarding.tours.mySpeaking.recordings.title",
        title: "Replay and review",
        contentKey: "pages.onboarding.tours.mySpeaking.recordings.content",
        content:
          "Expand a topic to see each question, play back your recording, and view the detailed AI feedback. Use 'Practice again' to record a fresh attempt and improve your score.",
        placement: "top",
        hideIfMissing: true,
        data: { icon: Play },
      },
    ],
  },

  streaks: {
    routeKey: "streaks",
    steps: [
      {
        target: "body",
        titleKey: "pages.onboarding.tours.streaks.rewards.title",
        title: "Streaks & rewards",
        contentKey: "pages.onboarding.tours.streaks.rewards.content",
        content:
          "Keep your daily streak alive and earn XP to level up. Challenge rewards are claimed automatically — open your avatar menu (top right) anytime to see your stats.",
        placement: "center",
        isFixed: true,
        data: { icon: Flame },
      },
    ],
  },
};

/** Get the tour config for a routeKey, or null if none is defined. */
export const getPageTour = (routeKey: string): PageTour | null => PAGE_TOURS[routeKey] ?? null;